/**
 * Per-product cart quantity limits (total qty across all variants).
 * Configure in KROWN.settings.product_cart_limits (theme.liquid) and product-cart-limit-vars.liquid.
 */
(function () {
  const DEFAULT_LIMITS = {
    '9494635053359': 40,
  };

  function getLimits() {
    const fromTheme = window.KROWN?.settings?.product_cart_limits;
    if (fromTheme && typeof fromTheme === 'object') {
      return fromTheme;
    }
    return DEFAULT_LIMITS;
  }

  function normalizeProductId(productId) {
    const id = parseInt(String(productId || ''), 10);
    return Number.isFinite(id) ? String(id) : '';
  }

  function hasLimit(productId) {
    const id = normalizeProductId(productId);
    return id !== '' && Object.prototype.hasOwnProperty.call(getLimits(), id);
  }

  function getLimit(productId) {
    const id = normalizeProductId(productId);
    return id !== '' ? getLimits()[id] ?? null : null;
  }

  function sumProductQtyInCart(cart, productId, excludeLineKey) {
    const id = normalizeProductId(productId);
    if (!cart || !Array.isArray(cart.items) || id === '') {
      return 0;
    }

    return cart.items.reduce((total, item) => {
      if (normalizeProductId(item.product_id) !== id) {
        return total;
      }
      if (excludeLineKey && item.key === excludeLineKey) {
        return total;
      }
      return total + (parseInt(item.quantity, 10) || 0);
    }, 0);
  }

  function limitMessage(limit) {
    const template =
      (window.KROWN &&
        KROWN.settings &&
        KROWN.settings.locales &&
        KROWN.settings.locales.product_cart_quantity_limit) ||
      'You can only add up to {{ limit }} of this product to your cart (across all variants).';
    return template.replace(/\{\{\s*limit\s*\}\}/g, String(limit));
  }

  function validateAdd(cart, productId, addQty) {
    const limit = getLimit(productId);
    if (limit == null) {
      return { ok: true };
    }

    const qtyToAdd = Math.max(0, parseInt(addQty, 10) || 0);
    const currentTotal = sumProductQtyInCart(cart, productId);
    const nextTotal = currentTotal + qtyToAdd;

    if (nextTotal > limit) {
      return {
        ok: false,
        message: limitMessage(limit),
        remaining: Math.max(0, limit - currentTotal),
        limit,
        currentTotal,
      };
    }

    return { ok: true, remaining: limit - nextTotal, limit, currentTotal };
  }

  function validateLineUpdate(cart, productId, lineKey, newQty) {
    const limit = getLimit(productId);
    if (limit == null) {
      return { ok: true };
    }

    const requestedQty = Math.max(0, parseInt(newQty, 10) || 0);
    const otherLinesTotal = sumProductQtyInCart(cart, productId, lineKey);
    const nextTotal = otherLinesTotal + requestedQty;

    if (nextTotal > limit) {
      const maxForLine = Math.max(0, limit - otherLinesTotal);
      return {
        ok: false,
        message: limitMessage(limit),
        maxForLine,
        limit,
        currentTotal: otherLinesTotal,
      };
    }

    return { ok: true, maxForLine: requestedQty, limit };
  }

  function validateCart(cart) {
    const limits = getLimits();
    const violations = [];

    Object.keys(limits).forEach((productId) => {
      const limit = parseInt(limits[productId], 10);
      const total = sumProductQtyInCart(cart, productId);
      if (total > limit) {
        violations.push({ productId, limit, total, message: limitMessage(limit) });
      }
    });

    return violations;
  }

  function fetchCart() {
    return fetch(`${KROWN.settings.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
    }).then((response) => response.json());
  }

  function checkAdd(productId, addQty) {
    return fetchCart().then((cart) => validateAdd(cart, productId, addQty));
  }

  function checkLineUpdate(productId, lineKey, newQty) {
    return fetchCart().then((cart) => validateLineUpdate(cart, productId, lineKey, newQty));
  }

  function showCheckoutLimitAlert(message) {
    const form = document.querySelector('form#cart.cart__form');
    if (!form) {
      alert(message);
      return;
    }
    form.querySelector('.alert--product-cart-limit')?.remove();
    const alertEl = document.createElement('span');
    alertEl.className = 'alert alert--error alert--product-cart-limit';
    alertEl.innerHTML = message;
    form.prepend(alertEl);
    alertEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  function bindCheckoutGuard() {
    let checkoutBypass = false;

    document.addEventListener(
      'click',
      (e) => {
        if (checkoutBypass) {
          return;
        }

        const btn = e.target.closest(
          '#CheckOut, button[data-js-cart-page-checkout], button[name="checkout"][form="cart"]'
        );
        if (!btn || btn.disabled) {
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        fetchCart()
          .then((cart) => {
            const violations = validateCart(cart);
            if (violations.length > 0) {
              showCheckoutLimitAlert(violations.map((v) => v.message).join(' '));
              return;
            }
            checkoutBypass = true;
            btn.click();
            checkoutBypass = false;
          })
          .catch(() => {
            checkoutBypass = true;
            btn.click();
            checkoutBypass = false;
          });
      },
      true
    );
  }

  function refreshQtySelectorMax(scopeEl, maxQty) {
    if (!scopeEl) return;
    const qtyInput = scopeEl.querySelector('.qty-selector');
    if (!qtyInput) return;

    const max = Math.max(0, parseInt(maxQty, 10) || 0);
    if (max <= 0) {
      qtyInput.setAttribute('max', '1');
      qtyInput.value = '1';
    } else {
      qtyInput.setAttribute('max', String(max));
      if (parseInt(qtyInput.value, 10) > max) {
        qtyInput.value = String(max);
      }
    }

    const current = parseInt(qtyInput.value, 10) || 1;
    const effectiveMax = max <= 0 ? 1 : max;
    const plus = scopeEl.querySelector('.qty-plus');
    const minus = scopeEl.querySelector('.qty-minus');
    if (plus) plus.classList.toggle('disabled', current >= effectiveMax);
    if (minus) minus.classList.toggle('disabled', current <= 1);
  }

  function refreshProductPageLimits() {
    document.querySelectorAll('[data-js-product-component][data-id]').forEach((productPage) => {
      const productId = productPage.dataset.id;
      if (!hasLimit(productId)) return;

      fetchCart()
        .then((cart) => {
          const limit = getLimit(productId);
          const currentTotal = sumProductQtyInCart(cart, productId);
          const remaining = Math.max(0, limit - currentTotal);
          refreshQtySelectorMax(productPage, remaining > 0 ? remaining : 1);

          const addBtn = productPage.querySelector('[data-js-product-add-to-cart]');
          if (addBtn) {
            if (remaining <= 0) {
              addBtn.setAttribute('disabled', 'disabled');
              addBtn.classList.add('disabled');
            } else {
              addBtn.removeAttribute('disabled');
              addBtn.classList.remove('disabled');
            }
          }
        })
        .catch(() => {});
    });
  }

  function refreshAllLimits() {
    refreshProductPageLimits();
  }

  function init() {
    refreshAllLimits();
    bindCheckoutGuard();
    document.addEventListener('cart-updated', refreshAllLimits);
    document.addEventListener('add-to-cart', refreshAllLimits);
  }

  window.JoelsProductCartLimits = {
    DEFAULT_LIMITS,
    getLimits,
    hasLimit,
    getLimit,
    sumProductQtyInCart,
    validateAdd,
    validateLineUpdate,
    validateCart,
    checkAdd,
    checkLineUpdate,
    refreshAllLimits,
    limitMessage,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
